import React, { useEffect, useState } from 'react';
import './App.css';
import socketIOClient from 'socket.io-client';
import { gql, ApolloClient, InMemoryCache, useMutation } from '@apollo/client';
import GraphComponent from './GraphComponent'; // Import the graph component
import MqttCommandSender from "./MqttCommandSender";
import logo from './assets/logo.png'; // Import the logo
import { IoArrowBack } from 'react-icons/io5';


//Update this to have .env variable to determine if you are running on a local machine or tying into the server//
const socket = socketIOClient('wss://mqtt-logger.cub.bi/');

// Set up Apollo Client
const client = new ApolloClient({
    uri: 'https://cubbi.app/graphql', // Replace with your GraphQL server URL
    cache: new InMemoryCache(),
});

const renderSignalIcon = (signalStrength) => {
    if (signalStrength === 'N/A') return <span>📡 No Data</span>;
    if (signalStrength > -60) return <span>📶 Strong</span>;  // Excellent
    if (signalStrength > -75) return <span>📶 Medium</span>;  // Good
    if (signalStrength > -90) return <span>📶 Weak</span>;    // Poor
    return <span>📶 Very Weak</span>;                         // Barely Connected
};

const GET_FRIDGES = gql`
    query fridges {
        fridges(inMarket: true) {
          edges {
            node {
              id
              name
              chipId
            }
          }
        }
      }
`;



export const LOGIN = gql`
  mutation tokenAuth($email: String!, $password: String!) {
    tokenAuth(email: $email, password: $password) {
      token
      refreshToken
      errors
      user {
        firstName
        lastName
        email
        isAdmin
        isDriver
      }
    }
  }
`;


function App() {
    const [messages, setMessages] = useState([]);
    const [topics, setTopics] = useState([]);
    const [selectedTopic, setSelectedTopic] = useState(null);
    const [topicData, setTopicData] = useState({});
    const [graphData, setGraphData] = useState([]);
    const [chipIdMap, setChipIdMap] = useState({});
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    



    const [login, { loading, error }] = useMutation(LOGIN, {
        client,
        onCompleted: (data) => {
            if (!data.tokenAuth.errors) {
                setIsAuthenticated(true);
            } else {
                alert('Invalid credentials!');
            }
        },
    });


    useEffect(() => {
        client
            .query({ query: GET_FRIDGES })
            .then((response) => {
                const fridges = response.data.fridges.edges;
                const map = {};
                fridges.forEach((fridge) => {
                    fridge = fridge.node;
                    map[`F${fridge.chipId}`] = fridge.name;
                });
                setChipIdMap(map);
            })
            .catch((error) => console.error('Error fetching fridge names:', error));
    }, []);

    useEffect(() => {
        socket.on('mqtt_message', (msg) => {
            const topic = msg['Topic'].toString();
            const message = parseFloat(msg['Message'].toString().split(',')[0]);
            const timestamp = msg['TimeStamp'].toString();
            const finalMessage = `${timestamp}: ${message}`;

            if (topic.startsWith('F')) {
                setMessages((prevMessages) => [
                    { topic, finalMessage },
                    ...prevMessages.slice(0, 1000),
                ]);

                setTopics((prevTopics) =>
                    prevTopics.includes(topic) ? prevTopics : [...prevTopics, topic]
                );

                setTopicData((prevData) => {
                    const updatedData = { ...prevData };
                    if (!updatedData[topic]) updatedData[topic] = [];
                    updatedData[topic].push({ date: timestamp, value: message });
                    return updatedData;
                });
            } else if (topic.startsWith('S')) {
                const chipId = topic.substring(1);
                const signalStrength = parseInt(message, 10);

                setChipIdMap((prevMap) => ({
                    ...prevMap,
                    [`S${chipId}`]: signalStrength,
                }));
            }
        });

        return () => {
            socket.off('mqtt_message');
        };
    }, []);

    useEffect(() => {
        if (!selectedTopic) return;

        fetch(`https://mqtt-logger.cub.bi/api/history/${selectedTopic}`)
            .then(res => res.json())
            .then(data => {
                console.log('Fetched data:', data); // 👀 Check this in DevTools
                const graphPoints = data.map(entry => ({
                    date: entry.timestamp,
                    value: entry.value
                }));
                setGraphData(graphPoints);
            })
            .catch(err => {
                console.error('Failed to load historical data:', err);
                setGraphData([]);
            });
    }, [selectedTopic]);

    const filteredMessages = selectedTopic
        ? messages.filter((msg) => msg.topic === selectedTopic)
        : messages;

    const handleLogin = () => {
        login({ variables: { email: username, password } });
    };
    
    if (!isAuthenticated) {
        return (
            <div className="login-container">
                <div className="login-card">
                    <img src={logo} alt="Logo" className="login-logo" />
                    <h2>Cubbi Monitoring Login</h2>
                    <input 
                        type="text" 
                        placeholder="Username" 
                        value={username} 
                        onChange={(e) => setUsername(e.target.value)} 
                        className="login-input"
                    />
                    <input 
                        type="password" 
                        placeholder="Password" 
                        value={password} 
                        onChange={(e) => setPassword(e.target.value)} 
                        className="login-input"
                    />
                    <button onClick={handleLogin} className="login-button">Login</button>
                </div>
            </div>

        );
    }
    return (
        <div className="App">
           <header className="App-header flex items-center gap-4 px-6 py-4 bg-green-600 border-b border-green-700 shadow">
                <img src={logo} alt="Cubbi Logo" className="logo-header" />
            </header>

            <main className="main-container">
                {!selectedTopic ? (
                    <div className="row">
                        <div className="topic-container flex-1">
                            <h2>Subscribed Topics</h2>
                            <ul className="topic-list">
                                {topics.length === 0 ? (
                                    <p>No topics available</p>
                                ) : (
                                    topics.map((topic, index) => {
                                        const fridgeName = chipIdMap[topic] || `Unknown (${topic})`;
                                        const signalTopic = `S${topic.substring(1)}`;
                                        const signalStrength = chipIdMap[signalTopic] || 'N/A';

                                        return (
                                            <li
                                                key={index}
                                                className={`topic ${selectedTopic === topic ? 'active' : ''}`}
                                                onClick={() => setSelectedTopic(topic)}
                                            >
                                                {fridgeName} {renderSignalIcon(signalStrength)}
                                            </li>

                                        );
                                    })
                                )}
                            </ul>
                        </div>
                        <div className="message-container flex-2">
                            {filteredMessages.length === 0 ? (
                                <p>No messages yet</p>
                            ) : (
                                <ul>
                                    <h2>Incoming Messages</h2>
                                    {filteredMessages.map((msg, index) => (
                                        <li key={index} className="message">
                                            <strong>
                                              {chipIdMap[msg.topic] || `Unknown`} ({msg.topic})
                                            </strong>: {msg.finalMessage}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="selected-topic-view">
                        <button className="clear-filter" onClick={() => setSelectedTopic(null)}>
                            <IoArrowBack size={20} />
                        </button>
                        <div className="row">
                            <div className="graph-container flex-1">
                                {graphData.length > 0 && <GraphComponent data={graphData} heading="Temperature" />}
                            </div>
                            <div className="message-container-graph flex-1">
                                {filteredMessages.length === 0 ? (
                                    <p>No messages yet</p>
                                ) : (
                                    <div>
                                        <div>
                                            <ul>
                                                {filteredMessages.map((msg, index) => (
                                                    <li key={index} className="message">
                                                        <strong>{chipIdMap[msg.topic] || `Unknown (${msg.topic})`}:</strong>{' '}
                                                        {msg.finalMessage}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
}

export default App;
